import { cloneDeep, isEqual, sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { computed, onBeforeMount, provide, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';

import { crossbeamApi } from '@/api';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import {
  EMPTY_MAPPING,
  MAP_TRAY_TO_INTEGRATION_PROFILE_TYPE,
} from '@/constants/integrations';
import { captureException } from '@/errors';
import { ProvidedTrayState } from '@/injectionKeys/integration_profiles';
import {
  useFeatureFlagStore,
  useFlashesStore,
  useIntegrationProfileStore,
  usePartnerCloudStore,
  usePartnersStore,
} from '@/stores';
import {
  EcosystemCrossbeamField,
  EcosystemFieldMappings,
  EcosystemFieldsPush,
  PartnerFieldMapping,
  PartnerOption,
  PutPartnerFieldMappingsPayload,
  SharedPartnerField,
  TrayPayload,
} from '@/types/integration_profiles';
import {
  Mapping,
  TabValue,
  TrayExternalId,
  TrayTag,
} from '@/types/integrations';

export default function useTrayConfig() {
  const partnerCloudStore = usePartnerCloudStore();
  const route = useRoute();

  const { addErrorFlash, addSuccessFlash } = useFlashesStore();
  const { hasFeatureFlag } = useFeatureFlagStore();

  const pathTrayTag = computed(() => route.params.tray_tag as TrayTag);

  const enabledTray = computed(() => {
    const foundTray = partnerCloudStore.enabledTray.find(
      (trayIntegration) => trayIntegration?.trayTag === pathTrayTag.value,
    );
    return foundTray;
  });

  const isIntegrationEnabled = ref(enabledTray?.value?.enabled ?? false);

  function parseConfigValue<T = boolean | EcosystemFieldsPush>(
    externalId: TrayExternalId,
    defaultValue: T,
  ): T {
    const raw = enabledTray.value?.configValues?.find(
      (c) => c.externalId === externalId,
    )?.value;
    if (!raw) return defaultValue;
    try {
      const parsed = JSON.parse(raw);
      return parsed;
    } catch {
      return defaultValue;
    }
  }

  const integrationProfileType = computed(() => {
    if (
      pathTrayTag.value &&
      pathTrayTag.value in MAP_TRAY_TO_INTEGRATION_PROFILE_TYPE
    ) {
      const value = MAP_TRAY_TO_INTEGRATION_PROFILE_TYPE[pathTrayTag.value];
      if (!value) {
        throw Error('Integration profile type not found');
      }
      return value;
    }

    return undefined;
  });

  const friendlyNameMap = {
    salesforce: 'Salesforce',
    hubspot: 'HubSpot',
    clari: 'Clari',
    microsoft_dynamics: 'Microsoft Dynamics',
    crossbeam_copilot: 'Crossbeam Copilot',
    snowflake: 'Snowflake',
  } as const;

  const friendlyName = computed(() => {
    if (!integrationProfileType.value) return '';
    return friendlyNameMap[integrationProfileType.value];
  });

  const hasTabUI = computed(
    () =>
      hasFeatureFlag('temp_integrations_tab_ui') ||
      hasFeatureFlag('temp_partner_field_mappings'),
  );

  const rawInitialState = {
    isEnabled: enabledTray.value?.enabled ?? false,
    oppPush: parseConfigValue<boolean>('external_opps-enabled', false),
    customObjectPush: parseConfigValue<boolean>(
      'external_push_overlaps',
      false,
    ),
    partnerFieldMappingsPush: parseConfigValue<boolean>(
      'external_push_partner_field_mappings',
      false,
    ),
    accountPush: parseConfigValue<EcosystemFieldsPush>(
      'external_push_ecosystem_fields',
      'NONE',
    ),
    ecosystemMaps: parseConfigValue<EcosystemFieldMappings>(
      'external_ecosystem_fields',
      null,
    ),
    profileId: parseConfigValue<string | undefined>(
      'external_crossbeam-profile-id',
      undefined,
    ),
    partnerFieldMappings: [EMPTY_MAPPING] as Mapping[],
    sharedPartnerFields: [] as PartnerOption[],
    isProfileSettingDirty: false,
  };

  // Deep‑clone rawInitialState so Vue’s reactivity doesn’t share any nested references.
  // That way, when we diff mappings later, we’re comparing truly independent objects.
  const initialState = reactive(cloneDeep(rawInitialState));

  // Same deal for the “live” state – start with a fresh clone so all nested properties
  // can be tracked and diffed without accidental shared references.
  const state = reactive({
    ...cloneDeep(rawInitialState),
    isSaving: false,
    isDeleting: false,
    profileType: integrationProfileType.value,
    saveTrayConfig,
    savePartnerFieldMappings,
    resetState,
    saveTabSettings,
    friendlyName: friendlyName.value,
    enabledTray,
    hasTrayConfigChanges: false,
    isPartnerFieldMappingsDirty: false,
    isProfileSettingDirty: false,
  });

  // filter and trim the partner field mappings to make it easier to compare
  function filterAndTrimMappings(mappings: Mapping[]) {
    return mappings
      .filter(
        (m) => m.partner_source_field_id !== null && m.source_field_id !== null,
      )
      .map((m) => ({
        partner_source_field_id: m.partner_source_field_id,
        source_field_id: m.source_field_id,
      }));
  }

  const filteredInitialMappings = computed(() =>
    filterAndTrimMappings(initialState.partnerFieldMappings),
  );

  const filteredCurrentMappings = computed(() =>
    filterAndTrimMappings(state.partnerFieldMappings),
  );

  const isPartnerFieldMappingsDirty = computed(
    () =>
      !isEqual(filteredCurrentMappings.value, filteredInitialMappings.value),
  );

  const newIsTrayConfigUpdateRequired = computed(() => {
    if (state.isEnabled !== initialState.isEnabled) return true;
    if (state.oppPush !== initialState.oppPush) return true;
    if (state.customObjectPush !== initialState.customObjectPush) return true;
    if (
      state.partnerFieldMappingsPush !== initialState.partnerFieldMappingsPush
    ) {
      return true;
    }
    if (state.accountPush !== initialState.accountPush) return true;

    if (!isEqual(state.ecosystemMaps, initialState.ecosystemMaps)) {
      return Array.isArray(state.ecosystemMaps)
        ? state.ecosystemMaps.every((m) => !!m.crm_field)
        : true;
    }
    return false;
  });

  // Assign computed property after both are declared
  Object.defineProperty(state, 'hasTrayConfigChanges', {
    get: () => newIsTrayConfigUpdateRequired.value,
  });
  Object.defineProperty(state, 'isPartnerFieldMappingsDirty', {
    get: () => isPartnerFieldMappingsDirty.value,
  });

  function resetState() {
    const newData = cloneDeep(initialState);
    Object.assign(state, newData);
  }

  provide(ProvidedTrayState, state);

  const integrationProfileId = computed(() => {
    if (!integrationProfileType.value) return null;

    return integrationProfileStore.getIntegrationProfileByType(
      integrationProfileType.value,
    )?.profile_id;
  });

  const integrationProfileStore = useIntegrationProfileStore();
  const createTrayConfigPayload = (): TrayPayload => {
    const profileId = integrationProfileId.value;

    if (!profileId) {
      throw new Error('Integration profile settings not found');
    }

    if (!enabledTray?.value) {
      throw new Error('Tray integration info not found');
    }

    const config: TrayPayload['config'] = {
      integration_profile_id: profileId,
      start_timestamp: DateTime.now().plus({ minutes: 15 }).toISO(),
      enabled: state.isEnabled,
      opps_enabled: state.oppPush,
      push_overlaps: state.customObjectPush,
      push_ecosystem_fields: state.accountPush,
      push_partner_field_mappings: state.partnerFieldMappingsPush,
    };

    initialState.partnerFieldMappingsPush = state.partnerFieldMappingsPush;
    initialState.isEnabled = state.isEnabled;
    initialState.oppPush = state.oppPush;
    initialState.customObjectPush = state.customObjectPush;
    initialState.accountPush = state.accountPush;
    initialState.ecosystemMaps = state.ecosystemMaps;

    if (integrationProfileType.value === 'salesforce' && state.ecosystemMaps) {
      config.ecosystem_fields = state.ecosystemMaps.filter(
        (
          m,
        ): m is {
          crossbeam_field: EcosystemCrossbeamField;
          crm_field: string;
        } => Boolean(m.crm_field),
      );
    }

    return {
      config,
      instance_name: enabledTray.value.name,
      instance_id: enabledTray.value.id,
    };
  };

  const updateTrayInstanceConfig = async (payload: TrayPayload) => {
    const { error } = await crossbeamApi.PUT('/v0.1/tray-integrations', {
      body: payload,
    });
    if (error) {
      captureException(error);
      addErrorFlash({
        message: 'Failed to save integration settings',
      });
    }
  };

  async function savePartnerFieldMappings() {
    if (!integrationProfileId.value) return;

    const payload = state.partnerFieldMappings
      .filter(
        (m) => m.source_field_id !== null && m.partner_source_field_id !== null,
      )
      .map((m) => ({
        source_field_id: m.source_field_id,
        partner_source_field_id: m.partner_source_field_id,
        partner_organization_uuid: m.partner_organization_uuid,
      })) as PutPartnerFieldMappingsPayload;

    const { data } = await crossbeamApi.PUT(
      '/v0.1/integration-profiles/{profile_id}/partner-field-mappings',
      {
        body: payload,
        params: {
          path: {
            profile_id: integrationProfileId.value,
          },
        },
      },
    );
    setPartnerFieldMappings(data?.items ?? []);
  }

  function setPartnerFieldMappings(data: PartnerFieldMapping[]) {
    const mappingsWithEmpty = data.map((m) => ({
      ...m,
      empty: false,
    }));
    initialState.partnerFieldMappings = cloneDeep(mappingsWithEmpty);
    state.partnerFieldMappings = cloneDeep(mappingsWithEmpty);
  }

  async function saveTrayConfig() {
    try {
      const payload = createTrayConfigPayload();
      await updateTrayInstanceConfig(payload);
      await partnerCloudStore.refreshPartnerCloudStore();
    } catch (err) {
      addErrorFlash({
        message: 'Failed to save tray configuration',
      });
      captureException(err);
    }
  }

  const partnersStore = usePartnersStore();

  const mapPartnersToFields = (field: SharedPartnerField) => {
    const { organization_uuid, nickname, source_field_id, column } = field;
    const partner = partnersStore.getPartnerOrgByUuid(organization_uuid);
    if (!partner) {
      return null;
    }
    return {
      ...partner,
      users: partner.users ?? [],
      tags: partner.tags ?? [],
      label: nickname ?? column,
      value: source_field_id,
    };
  };

  const hasPartnerFieldMappings = computed(() =>
    hasFeatureFlag(FEATURE_FLAG.TEMP_PARTNER_FIELD_MAPPINGS),
  );

  onBeforeMount(async () => {
    if (!hasTabUI.value || !integrationProfileId.value) return;

    const [sharedResponse, mappingResponse] = await Promise.all([
      crossbeamApi.GET(
        `/v0.1/integration-profiles/{profile_id}/shared-partner-fields`,
        {
          params: {
            path: {
              profile_id: integrationProfileId.value,
            },
          },
        },
      ),
      crossbeamApi.GET(
        `/v0.1/integration-profiles/{profile_id}/partner-field-mappings`,
        {
          params: {
            path: {
              profile_id: integrationProfileId.value,
            },
          },
        },
      ),
    ]);
    if (sharedResponse.data) {
      const mappedSharedFields = sharedResponse.data.items.reduce(
        (acc, curr) => {
          const mappedField = mapPartnersToFields(curr);
          if (mappedField) acc.push(mappedField);
          return acc;
        },
        [] as PartnerOption[],
      );
      const sortedSharedFields = sortBy(mappedSharedFields, ['name', 'label']); // sort by partner name then field label
      initialState.sharedPartnerFields = sortedSharedFields;
      state.sharedPartnerFields = sortedSharedFields;
    }

    const existingMappings = mappingResponse.data?.items;
    if (!existingMappings?.length) return;

    setPartnerFieldMappings(existingMappings);
  });

  async function saveTabSettings(activeTab: TabValue) {
    state.isSaving = true;

    const promises = [];

    if (state.hasTrayConfigChanges) {
      promises.push(saveTrayConfig());
    }

    if (activeTab === 'account-object' && isPartnerFieldMappingsDirty.value) {
      promises.push(savePartnerFieldMappings());
    }

    try {
      await Promise.all(promises);
    } catch (error) {
      captureException(error);
      addErrorFlash({
        message: 'Something went wrong',
        description: 'Failed to save settings',
      });
    }

    let toastMessage = '';
    switch (activeTab) {
      case 'account-object':
        toastMessage =
          integrationProfileType.value === 'salesforce'
            ? 'Account Object settings saved successfully'
            : 'Company Object settings saved successfully';
        break;
      case 'custom-object':
        toastMessage = 'Custom Object settings saved successfully';
        break;
      default:
        toastMessage = 'Settings saved successfully';
        break;
    }
    addSuccessFlash({
      message: toastMessage,
    });

    state.isSaving = false;
  }

  return {
    integrationProfileType,
    isTrayConfigUpdateRequired: newIsTrayConfigUpdateRequired,
    enabledTray,
    isIntegrationEnabled,
    hasPartnerFieldMappings,
    hasTabUI,
    saveTrayConfig,
    savePartnerFieldMappings,
    state,
  };
}
